import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    searchSellerSales,
    resendSellerSaleMessage,
    getMyEarnings,
    getSellerSaleDetail,
    tempEditSaleCustomer,
} from '../services/sellerSalesApi';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from '../components/ui/sheet';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const getMyTotals = async () => {
    const { data } = await axios.get(`${API}/seller/my-totals`, { headers: authHeaders() });
    return data;
};
import {
    MagnifyingGlass,
    SpinnerGap,
    Receipt,
    User,
    Envelope,
    Phone,
    Calendar,
    PaperPlaneTilt,
    CurrencyDollar,
    CreditCard,
    Gift,
    Money,
    X,
    ArrowsClockwise,
    ChatCircleText,
    XCircle,
    Warning,
    PencilSimple,
    Check,
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

const PAYMENT_LABELS = {
    cash: 'Efectivo',
    credit_card: 'Tarjeta de Crédito',
    debit_card: 'Tarjeta de Débito',
    transfer: 'Transferencia',
    courtesy: 'Cortesía',
};

const fmtMoney = (n) =>
    new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(n) || 0);

const fmtDate = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleString('es-MX', {
            timeZone: 'America/Mexico_City',
            day: '2-digit', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    } catch { return iso; }
};

const MySalesPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const [query, setQuery] = useState('');
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedSale, setSelectedSale] = useState(null);
    const [showEarnings, setShowEarnings] = useState(false);
    const [earnings, setEarnings] = useState(null);
    const [loadingEarnings, setLoadingEarnings] = useState(false);
    const [cancelingSale, setCancelingSale] = useState(null);
    const [cancelReason, setCancelReason] = useState('');
    const [submittingCancel, setSubmittingCancel] = useState(false);

    const isSeller = user?.role === 'seller';
    const L = isSeller
        ? { titleOne: 'activación', titleMany: 'activaciones', titleCap: 'Mis Activaciones' }
        : { titleOne: 'venta', titleMany: 'ventas', titleCap: 'Mis Ventas' };

    // Range param: today | current_cut | (none → default: current_cut para promotoras
    // muestra todas las activaciones del corte actual, no sólo la última).
    const rangeParam = new URLSearchParams(location.search).get('range') || 'current_cut';

    const load = useCallback(async (q = '', range = '') => {
        setLoading(true);
        try {
            const data = await searchSellerSales(q, range);
            setSales(data.sales || []);
        } catch (e) {
            toast.error(`No se pudieron cargar las ${L.titleMany}`);
        } finally {
            setLoading(false);
        }
    }, [L.titleMany]);

    useEffect(() => {
        load('', rangeParam);
    }, [load, rangeParam]);

    // Debounced search (search overrides any range filter)
    useEffect(() => {
        if (!query) return;
        const t = setTimeout(() => {
            load(query.trim(), '');
        }, 320);
        return () => clearTimeout(t);
    }, [query, load]);

    const handleShowEarnings = async () => {
        setShowEarnings(true);
        setLoadingEarnings(true);
        try {
            const [data, totals] = await Promise.all([
                getMyEarnings(),
                getMyTotals(),
            ]);
            setEarnings({ ...data, totalsByMethod: totals });
        } catch (e) {
            toast.error('No se pudieron cargar las ganancias');
        } finally {
            setLoadingEarnings(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="my-sales-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#8B8474] font-semibold mb-1">
                        Mi área
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1F2D4B] flex items-center gap-2">
                        <Receipt size={28} weight="duotone" className="text-[#FB7185]" />
                        {L.titleCap}
                    </h1>
                    <p className="text-sm text-[#4B5878] mt-1">
                        {rangeParam === 'today'
                            ? `Mostrando todas tus ${L.titleMany} de HOY.`
                            : rangeParam === 'current_cut'
                                ? `Mostrando todas tus ${L.titleMany} del corte actual.`
                                : `Por defecto verás tu última ${L.titleOne}. Busca por nombre o folio para encontrar otras.`}
                    </p>
                </div>
                <Button
                    onClick={handleShowEarnings}
                    data-testid="show-earnings-btn"
                    className="h-10 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md"
                >
                    <CurrencyDollar size={14} weight="bold" className="mr-1.5" />
                    Totales
                </Button>
            </div>

            {/* Search */}
            <div className="relative">
                <MagnifyingGlass
                    size={18}
                    weight="bold"
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B8474] pointer-events-none"
                />
                <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Buscar por nombre, teléfono, email o folio…"
                    data-testid="search-sales-input"
                    className="pl-10 h-11 border-[#ECE3D2] focus:border-[#1F2D4B] bg-white"
                />
                {query && (
                    <button
                        type="button"
                        onClick={() => setQuery('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8B8474] hover:text-[#1F2D4B]"
                    >
                        <X size={16} weight="bold" />
                    </button>
                )}
            </div>

            {!query && sales.length === 0 && (
                <div className="text-xs text-[#8B8474] flex items-center gap-2">
                    <Receipt size={12} weight="bold" />
                    No hay {L.titleMany} {rangeParam === 'today' ? 'hoy' : 'en el corte actual'}.
                </div>
            )}
            {rangeParam && rangeParam !== 'current_cut' && (
                <div className="flex items-center gap-2 text-xs">
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-[#FB7185]/10 text-[#FB7185] font-semibold">
                        {rangeParam === 'today' ? 'Filtro: Hoy' : 'Filtro: Corte actual'}
                    </span>
                    <button
                        type="button"
                        onClick={() => navigate('/my-sales', { replace: true })}
                        data-testid="clear-range-btn"
                        className="text-[#8B8474] hover:text-[#1F2D4B] underline"
                    >
                        Limpiar filtro
                    </button>
                </div>
            )}

            {/* Sales list */}
            {loading ? (
                <div className="text-center py-12 text-[#8B8474]">
                    <SpinnerGap size={24} className="animate-spin inline mr-2" />
                    Cargando…
                </div>
            ) : sales.length === 0 ? (
                <Card className="bg-white border border-dashed border-[#ECE3D2]">
                    <CardContent className="p-10 text-center">
                        <Receipt size={48} weight="duotone" className="mx-auto text-[#C8B996] mb-3" />
                        <p className="text-[#4B5878]">
                            {query
                                ? `No se encontraron ventas para "${query}".`
                                : rangeParam === 'today'
                                    ? `Aún no tienes ${L.titleMany} hoy.`
                                    : 'Aún no has registrado ventas.'}
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {sales.map((s) => (
                        <SaleRow
                            key={s.id}
                            sale={s}
                            onOpen={async () => {
                                // Open optimistically with list data, then enrich
                                // with full detail (which respects sales.view_contact_in_detail).
                                setSelectedSale(s);
                                try {
                                    const full = await getSellerSaleDetail(s.id);
                                    setSelectedSale((curr) => (curr && curr.id === s.id ? { ...curr, ...full } : curr));
                                } catch (_) { /* keep optimistic data */ }
                            }}
                        />
                    ))}
                </div>
            )}

            {/* Sale detail */}
            <Sheet open={!!selectedSale} onOpenChange={(open) => !open && setSelectedSale(null)}>
                <SheetContent
                    side="right"
                    className="w-full sm:max-w-lg overflow-y-auto bg-white"
                    data-testid="sale-detail-sheet"
                >
                    {selectedSale && (
                        <SaleDetail
                            sale={selectedSale}
                            onClose={() => setSelectedSale(null)}
                            onTicket={() => navigate(`/tickets/${selectedSale.id}`)}
                            onCancel={(s) => {
                                setCancelingSale(s);
                                setCancelReason('');
                            }}
                            onReload={() => load(query, rangeParam)}
                        />
                    )}
                </SheetContent>
            </Sheet>

            {/* Earnings panel */}
            <Sheet open={showEarnings} onOpenChange={setShowEarnings}>
                <SheetContent
                    side="right"
                    className="w-full sm:max-w-md overflow-y-auto bg-white"
                    data-testid="earnings-sheet"
                >
                    <SheetHeader>
                        <SheetTitle className="text-[#1F2D4B] flex items-center gap-2">
                            <CurrencyDollar size={22} weight="duotone" className="text-[#FB7185]" />
                            Totales del periodo
                        </SheetTitle>
                        <SheetDescription className="text-[#8B8474]">
                            Acumulado desde el último corte de caja. Es para tu control personal — al cerrar el corte entregarás el efectivo.
                        </SheetDescription>
                    </SheetHeader>

                    {loadingEarnings ? (
                        <div className="text-center py-12 text-[#8B8474]">
                            <SpinnerGap size={24} className="animate-spin inline mr-2" />
                            Cargando…
                        </div>
                    ) : earnings ? (
                        <div className="mt-6 space-y-4">
                            <Card className="bg-gradient-to-br from-[#1F2D4B] to-[#2E3F60] text-white border-0">
                                <CardContent className="p-5">
                                    <p className="text-xs uppercase tracking-wider text-white/70 font-semibold mb-1">
                                        Comisión estimada
                                    </p>
                                    <p className="text-4xl font-bold" data-testid="estimated-commission">
                                        {fmtMoney(earnings.estimated_commission)}
                                    </p>
                                    <p className="text-xs text-white/70 mt-2">
                                        {earnings.commission_percent}% sobre {fmtMoney(earnings.total_sales_amount)}
                                    </p>
                                </CardContent>
                            </Card>

                            <div className="grid grid-cols-2 gap-3">
                                <Card className="bg-white border border-[#ECE3D2]">
                                    <CardContent className="p-4">
                                        <p className="text-[10px] uppercase tracking-wider text-[#8B8474] font-semibold">
                                            Ventas
                                        </p>
                                        <p className="text-2xl font-bold text-[#1F2D4B] mt-0.5">
                                            {earnings.count}
                                        </p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-white border border-[#ECE3D2]">
                                    <CardContent className="p-4">
                                        <p className="text-[10px] uppercase tracking-wider text-[#8B8474] font-semibold">
                                            Total vendido
                                        </p>
                                        <p className="text-2xl font-bold text-[#1F2D4B] mt-0.5">
                                            {fmtMoney(earnings.total_sales_amount)}
                                        </p>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Breakdown by payment method */}
                            {earnings.totalsByMethod && (
                                <Card className="bg-white border border-[#ECE3D2]" data-testid="totals-by-method">
                                    <CardContent className="p-4 space-y-2.5">
                                        <p className="text-[10px] uppercase tracking-wider text-[#1F2D4B] font-semibold mb-1">
                                            Desglose por forma de pago
                                        </p>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-[#8B8474]">T. Crédito</span>
                                            <span className="font-mono font-semibold text-[#1F2D4B]" data-testid="total-credit">
                                                {fmtMoney(earnings.totalsByMethod.credit_card || 0)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between text-sm">
                                            <span className="text-[#8B8474]">T. Débito</span>
                                            <span className="font-mono font-semibold text-[#1F2D4B]" data-testid="total-debit">
                                                {fmtMoney(earnings.totalsByMethod.debit_card || 0)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between text-sm pt-2 border-t border-[#F4ECE0]">
                                            <span className="text-[#1F2D4B] font-semibold flex items-center gap-1.5">
                                                <Money size={14} weight="fill" /> Efectivo en tu posesión
                                            </span>
                                            <span className="font-mono font-bold text-[#FB7185] text-base" data-testid="total-cash">
                                                {fmtMoney(earnings.totalsByMethod.cash || 0)}
                                            </span>
                                        </div>
                                        {earnings.totalsByMethod.courtesy_count > 0 && (
                                            <div className="flex justify-between text-xs text-[#8B8474]">
                                                <span>Cortesías otorgadas</span>
                                                <span>{earnings.totalsByMethod.courtesy_count}</span>
                                            </div>
                                        )}
                                        <p className="text-[10px] text-[#8B8474] italic pt-1 border-t border-[#F4ECE0]">
                                            El efectivo es lo que tienes en tu posesión y debes entregar el día del corte.
                                        </p>
                                    </CardContent>
                                </Card>
                            )}

                            <div className="text-[11px] text-[#8B8474] bg-[#FFF8E7] border border-[#F5DEB3] rounded-md p-3">
                                Esta cifra se calcula sobre ventas <b>completadas</b> que aún no se han incluido en un corte pagado. Tu administrador genera el corte oficial.
                            </div>
                        </div>
                    ) : null}
                </SheetContent>
            </Sheet>

            {/* Cancel/Refund confirmation dialog */}
            {cancelingSale && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
                    onClick={() => !submittingCancel && setCancelingSale(null)}
                    data-testid="cancel-dialog-overlay"
                >
                    <div
                        className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                                <Warning size={20} weight="bold" className="text-rose-600" />
                            </div>
                            <div>
                                <h3 className="font-display text-lg font-bold text-[#1F2D4B]">
                                    Cancelar / Devolución
                                </h3>
                                <p className="text-xs text-[#8B8474] mt-1">
                                    Se cancelarán todos los certificados de esta venta y se generará un folio DEV de devolución.
                                </p>
                            </div>
                        </div>
                        <label className="block text-xs text-[#4B5878] font-semibold mb-1">Motivo (opcional)</label>
                        <textarea
                            value={cancelReason}
                            onChange={(e) => setCancelReason(e.target.value)}
                            placeholder="Ej: cliente solicita reembolso, error de captura..."
                            data-testid="cancel-reason-input"
                            className="w-full h-20 border border-[#ECE3D2] rounded-md p-2 text-sm focus:border-[#1F2D4B] outline-none"
                        />
                        <div className="flex gap-2 mt-4">
                            <Button
                                variant="outline"
                                onClick={() => setCancelingSale(null)}
                                disabled={submittingCancel}
                                data-testid="cancel-dialog-dismiss"
                                className="flex-1 border-[#ECE3D2]"
                            >
                                Volver
                            </Button>
                            <Button
                                onClick={async () => {
                                    setSubmittingCancel(true);
                                    try {
                                        await axios.post(
                                            `${API}/customer-directory/sales/${cancelingSale.id}/cancel`,
                                            { reason: cancelReason || null, refund_method: cancelingSale.payment_method || 'cash' },
                                            { headers: authHeaders() }
                                        );
                                        toast.success('Venta cancelada. Devolución registrada.');
                                        setCancelingSale(null);
                                        setSelectedSale(null);
                                        load(query.trim());
                                    } catch (e) {
                                        toast.error(e.response?.data?.detail || 'No se pudo cancelar la venta');
                                    } finally {
                                        setSubmittingCancel(false);
                                    }
                                }}
                                disabled={submittingCancel}
                                data-testid="cancel-dialog-confirm"
                                className="flex-1 bg-rose-600 hover:bg-rose-700 text-white"
                            >
                                {submittingCancel
                                    ? <><SpinnerGap size={14} className="animate-spin mr-1.5" /> Procesando…</>
                                    : <><XCircle size={14} weight="bold" className="mr-1.5" /> Confirmar cancelación</>}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const SaleRow = ({ sale, onOpen }) => {
    const customer = sale.customer || {};
    return (
        <button
            type="button"
            onClick={onOpen}
            data-testid={`sale-row-${sale.id}`}
            className="w-full text-left bg-white rounded-lg border-2 border-[#ECE3D2] hover:border-[#1F2D4B] transition-colors p-4"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-bold text-[#1F2D4B] truncate">{customer.name || 'Cliente'}</p>
                        {sale.is_courtesy && (
                            <Badge className="bg-[#FB7185] text-white text-[10px] hover:bg-[#FB7185]">
                                <Gift size={10} weight="bold" className="mr-1" />
                                Cortesía
                            </Badge>
                        )}
                    </div>
                    <p className="text-xs text-[#8B8474] flex items-center gap-1.5 flex-wrap">
                        <Calendar size={11} weight="bold" />
                        {fmtDate(sale.created_at)}
                    </p>
                    {sale.folios?.length > 0 && (
                        <p className="text-xs text-[#4B5878] mt-1.5 font-mono truncate">
                            {sale.folios.slice(0, 3).join(' · ')}
                            {sale.folios.length > 3 && ` · +${sale.folios.length - 3}`}
                        </p>
                    )}
                </div>
                <div className="text-right">
                    <p className="text-lg font-bold text-[#1F2D4B]">{fmtMoney(sale.total_amount)}</p>
                    <p className="text-[10px] uppercase tracking-wider text-[#8B8474] font-semibold mt-0.5">
                        {PAYMENT_LABELS[sale.payment_method] || sale.payment_method}
                    </p>
                </div>
            </div>
        </button>
    );
};

const SaleDetail = ({ sale, onClose, onTicket, onCancel, onReload }) => {
    const [customer, setCustomer] = useState(sale.customer || {});
    const [resending, setResending] = useState(null);
    const { can, user } = useAuth();

    // Permisos para cancelar — sólo aplica si la venta está en el corte actual
    const inCurrentCut = sale.in_current_cut !== false;
    const canCancelCurrentOwn = can('sales.refund_current_own') || can('sales.refund');
    const canCancel = inCurrentCut && canCancelCurrentOwn && !sale.is_cancelled;

    // ¿Puede ver teléfono y email del cliente en el detalle?
    const canSeeContact = can('sales.view_contact_in_detail');

    // Ventana temporal (15 min) para corregir datos del cliente ---------------
    const isAdmin = user?.role === 'admin';
    const canTempEdit = isAdmin || can('customers.edit_temporal_window');
    const WINDOW_SEC = 15 * 60;
    const saleCreatedAtMs = sale.created_at ? new Date(sale.created_at).getTime() : 0;
    // Recompute the remaining time from the raw timestamp on every tick — this
    // is the single source of truth. Using a delta counter would double-count
    // whenever React re-renders for other reasons.
    const computeRemaining = () => {
        if (!saleCreatedAtMs) return 0;
        const elapsed = Math.floor((Date.now() - saleCreatedAtMs) / 1000);
        return Math.max(0, WINDOW_SEC - elapsed);
    };
    const [secondsRemaining, setSecondsRemaining] = useState(computeRemaining);
    useEffect(() => {
        setSecondsRemaining(computeRemaining());
        if (!canTempEdit || !saleCreatedAtMs) return;
        const iv = setInterval(() => setSecondsRemaining(computeRemaining()), 1000);
        return () => clearInterval(iv);
    }, [canTempEdit, saleCreatedAtMs]);
    const withinWindow = canTempEdit && secondsRemaining > 0 && !sale.is_cancelled;
    const mmss = `${String(Math.floor(secondsRemaining / 60)).padStart(2, '0')}:${String(secondsRemaining % 60).padStart(2, '0')}`;

    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({ name: '', email: '', phone: '' });
    const [savingEdit, setSavingEdit] = useState(false);

    const openEdit = () => {
        setEditForm({
            name: customer.name || '',
            email: customer.email || '',
            phone: customer.phone || '',
        });
        setEditing(true);
    };
    const saveEdit = async () => {
        setSavingEdit(true);
        try {
            const changes = {};
            if ((editForm.name || '').trim() && editForm.name.trim() !== (customer.name || '')) changes.name = editForm.name.trim();
            if ((editForm.email || '').trim() !== (customer.email || '')) changes.email = editForm.email.trim() || null;
            if ((editForm.phone || '').trim() !== (customer.phone || '')) changes.phone = editForm.phone.trim() || null;
            if (Object.keys(changes).length === 0) {
                toast.info('No hay cambios');
                setEditing(false);
                return;
            }
            const res = await tempEditSaleCustomer(sale.id, changes);
            setCustomer(res.customer);
            toast.success(`Datos actualizados. Quedan ${Math.floor((res.window_seconds_remaining || 0) / 60)} min de la ventana.`);
            setEditing(false);
            onReload && onReload();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo editar');
        } finally {
            setSavingEdit(false);
        }
    };

    const handleResend = async (channel, type) => {
        const key = `${channel}-${type}`;
        setResending(key);
        try {
            await resendSellerSaleMessage(sale.id, { channel, type });
            toast.success(`Mensaje enviado por ${channel === 'email' ? 'correo' : channel === 'whatsapp' ? 'WhatsApp' : 'SMS'}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'No se pudo reenviar');
        } finally {
            setResending(null);
        }
    };

    return (
        <div className="pt-4">
            <SheetHeader>
                <SheetTitle className="text-[#1F2D4B] flex items-center gap-2">
                    <Receipt size={22} weight="duotone" className="text-[#FB7185]" />
                    Detalle de venta
                </SheetTitle>
                <SheetDescription className="text-[#8B8474] font-mono text-xs">
                    #{sale.id.slice(0, 8).toUpperCase()}
                </SheetDescription>
            </SheetHeader>

            <div className="mt-5 space-y-4">
                {/* Edit window banner — ONLY when window is active */}
                {withinWindow && (
                    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-md bg-[#EEF6FF] border border-[#3B82F6]/30" data-testid="temp-edit-window-banner">
                        <div className="flex items-center gap-2 text-[#1E40AF] text-xs">
                            <PencilSimple size={14} weight="bold" />
                            <span className="font-semibold">Ventana de edición activa</span>
                            <span className="text-[#1E40AF]/70">— quedan <span className="font-mono font-bold" data-testid="temp-edit-countdown">{mmss}</span></span>
                        </div>
                        {!editing && (
                            <button
                                type="button"
                                onClick={openEdit}
                                data-testid="open-temp-edit-btn"
                                className="text-[11px] font-bold px-2.5 py-1 rounded-md bg-[#1F2D4B] text-white hover:bg-[#0F1B3D]"
                            >
                                Editar datos
                            </button>
                        )}
                    </div>
                )}

                {/* Customer */}
                <Card className="bg-white border border-[#ECE3D2]">
                    <CardContent className="p-4">
                        <p className="text-[10px] uppercase tracking-wider text-[#8B8474] font-semibold mb-2">
                            Cliente
                        </p>
                        {editing ? (
                            <div className="space-y-2" data-testid="temp-edit-form">
                                <Input
                                    placeholder="Nombre"
                                    value={editForm.name}
                                    onChange={(e) => setEditForm(f => ({ ...f, name: e.target.value }))}
                                    data-testid="temp-edit-name"
                                    className="h-9 text-sm"
                                />
                                <Input
                                    placeholder="Email"
                                    value={editForm.email}
                                    onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))}
                                    data-testid="temp-edit-email"
                                    className="h-9 text-sm"
                                />
                                <Input
                                    placeholder="Teléfono (10 dígitos con lada)"
                                    value={editForm.phone}
                                    onChange={(e) => setEditForm(f => ({ ...f, phone: e.target.value }))}
                                    data-testid="temp-edit-phone"
                                    className="h-9 text-sm"
                                />
                                <div className="flex items-center gap-2 pt-1">
                                    <button
                                        type="button"
                                        onClick={saveEdit}
                                        disabled={savingEdit}
                                        data-testid="temp-edit-save-btn"
                                        className="flex-1 h-9 rounded-md bg-[#1F2D4B] text-white text-xs font-bold hover:bg-[#0F1B3D] disabled:opacity-50 inline-flex items-center justify-center gap-1"
                                    >
                                        {savingEdit ? <SpinnerGap size={12} className="animate-spin" /> : <Check size={12} weight="bold" />}
                                        Guardar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setEditing(false)}
                                        disabled={savingEdit}
                                        data-testid="temp-edit-cancel-btn"
                                        className="h-9 px-3 rounded-md border border-[#ECE3D2] text-[#4B5878] text-xs hover:bg-[#F8F4EC]"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <p className="font-bold text-[#1F2D4B] flex items-center gap-2">
                                    <User size={14} weight="bold" /> {customer.name || 'Cliente'}
                                </p>
                                {canSeeContact ? (
                                    <>
                                        {customer.email && (
                                            <p className="text-xs text-[#4B5878] mt-1 flex items-center gap-2" data-testid="customer-detail-email">
                                                <Envelope size={12} weight="bold" /> {customer.email}
                                            </p>
                                        )}
                                        {customer.phone && (
                                            <p className="text-xs text-[#4B5878] mt-1 flex items-center gap-2" data-testid="customer-detail-phone">
                                                <Phone size={12} weight="bold" /> {customer.phone}
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <p className="text-[11px] text-[#8B8474] italic mt-1" data-testid="customer-contact-hidden">
                                        Datos de contacto ocultos por privacidad. Si necesitas comunicarte con el cliente, consulta con tu supervisor.
                                    </p>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>

                {/* Totals */}
                <Card className="bg-white border border-[#ECE3D2]">
                    <CardContent className="p-4 space-y-2">
                        <div className="flex justify-between text-sm">
                            <span className="text-[#8B8474]">Total</span>
                            <span className="font-bold text-[#1F2D4B] text-lg">{fmtMoney(sale.total_amount)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                            <span className="text-[#8B8474]">Método de pago</span>
                            <span className="font-semibold text-[#1F2D4B] flex items-center gap-1.5">
                                {sale.is_courtesy ? (
                                    <><Gift size={14} weight="bold" /> Cortesía</>
                                ) : sale.payment_method === 'cash' ? (
                                    <><Money size={14} weight="bold" /> Efectivo</>
                                ) : (
                                    <><CreditCard size={14} weight="bold" /> {PAYMENT_LABELS[sale.payment_method]}</>
                                )}
                            </span>
                        </div>
                        {sale.card_last4 && (
                            <div className="flex justify-between text-xs text-[#8B8474]">
                                <span>Últimos 4 dígitos</span>
                                <span className="font-mono">**** {sale.card_last4}</span>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Certificates */}
                {sale.items?.length > 0 && (
                    <Card className="bg-white border border-[#ECE3D2]">
                        <CardContent className="p-4">
                            <p className="text-[10px] uppercase tracking-wider text-[#8B8474] font-semibold mb-2">
                                Certificados ({sale.items.length})
                            </p>
                            <div className="space-y-2">
                                {sale.items.map((it) => (
                                    <div key={it.id || it.folio} className="flex justify-between items-center text-sm">
                                        <div className="min-w-0 flex-1">
                                            <p className="font-semibold text-[#1F2D4B] truncate">{it.product_name}</p>
                                            <p className="text-[11px] text-[#8B8474] font-mono">{it.folio}</p>
                                        </div>
                                        <span className="text-[#1F2D4B] font-semibold">{fmtMoney(it.unit_price)}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Internal notes */}
                {sale.internal_notes && (
                    <Card className="bg-[#FFF8E7] border border-[#F5DEB3]">
                        <CardContent className="p-4">
                            <p className="text-[10px] uppercase tracking-wider text-[#92400E] font-semibold mb-1 flex items-center gap-1">
                                <ChatCircleText size={12} weight="bold" /> Notas internas
                            </p>
                            <p className="text-sm text-[#7A4F00] whitespace-pre-wrap">{sale.internal_notes}</p>
                        </CardContent>
                    </Card>
                )}

                {/* Resend actions */}
                <div className="space-y-2">
                    <p className="text-[10px] uppercase tracking-wider text-[#8B8474] font-semibold">
                        Reenviar
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                        <Button
                            variant="outline"
                            onClick={() => handleResend('email', 'receipt')}
                            disabled={!customer.has_email || resending === 'email-receipt'}
                            data-testid="resend-email-receipt-btn"
                            className="h-10 border-[#ECE3D2] text-[#1F2D4B] hover:bg-[#F4ECE0] justify-start"
                        >
                            {resending === 'email-receipt'
                                ? <SpinnerGap size={14} className="animate-spin mr-1.5" />
                                : <Envelope size={14} weight="bold" className="mr-1.5" />}
                            Recibo (correo)
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => handleResend('whatsapp', 'certificate')}
                            disabled={!customer.has_phone || resending === 'whatsapp-certificate'}
                            data-testid="resend-wa-cert-btn"
                            className="h-10 border-[#ECE3D2] text-[#1F2D4B] hover:bg-[#F4ECE0] justify-start"
                        >
                            {resending === 'whatsapp-certificate'
                                ? <SpinnerGap size={14} className="animate-spin mr-1.5" />
                                : <PaperPlaneTilt size={14} weight="bold" className="mr-1.5" />}
                            Certificado (WA)
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => handleResend('sms', 'certificate')}
                            disabled={!customer.has_phone || resending === 'sms-certificate'}
                            data-testid="resend-sms-cert-btn"
                            className="h-10 border-[#ECE3D2] text-[#1F2D4B] hover:bg-[#F4ECE0] justify-start"
                        >
                            {resending === 'sms-certificate'
                                ? <SpinnerGap size={14} className="animate-spin mr-1.5" />
                                : <ChatCircleText size={14} weight="bold" className="mr-1.5" />}
                            Certificado (SMS)
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => handleResend('email', 'certificate')}
                            disabled={!customer.has_email || resending === 'email-certificate'}
                            data-testid="resend-email-cert-btn"
                            className="h-10 border-[#ECE3D2] text-[#1F2D4B] hover:bg-[#F4ECE0] justify-start"
                        >
                            {resending === 'email-certificate'
                                ? <SpinnerGap size={14} className="animate-spin mr-1.5" />
                                : <Envelope size={14} weight="bold" className="mr-1.5" />}
                            Certificado (correo)
                        </Button>
                    </div>
                </div>

                <Button
                    onClick={onTicket}
                    data-testid="open-ticket-btn"
                    className="w-full h-11 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white"
                >
                    <Receipt size={14} weight="bold" className="mr-1.5" />
                    Ver / imprimir ticket
                </Button>

                {/* Cancel button — only if sale is in CURRENT cut and user has permission */}
                {canCancel ? (
                    <Button
                        onClick={() => onCancel && onCancel(sale)}
                        data-testid="cancel-sale-btn"
                        variant="outline"
                        className="w-full h-11 border-rose-300 text-rose-700 hover:bg-rose-50"
                    >
                        <XCircle size={14} weight="bold" className="mr-1.5" />
                        Cancelar / Devolución
                    </Button>
                ) : !inCurrentCut ? (
                    <div data-testid="cancel-blocked-past-cut" className="flex items-start gap-2 text-xs text-[#8B8474] bg-[#FFF8E7] border border-[#F5DEB3] rounded-md p-3">
                        <Warning size={14} weight="bold" className="text-[#92400E] mt-0.5 flex-shrink-0" />
                        <span>
                            Esta venta forma parte de un corte cerrado. Para cancelarla, comunícate con la oficina.
                        </span>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default MySalesPage;
